import type { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';

import { baseApi } from '../../../shared/api/baseApi';
import type { ApiPath } from '../../../shared/api/types';
import type { SearcherLimitsDto, YandexGraphsDto } from '../model/dto';
import { toSearcherLimits, toYandexGraphs } from '../model/mappers';
import type { ReportFile, SearcherLimit, SearcherLimitsQuery, YandexGraphs } from '../model/types';

const TAG = 'Report' as const;

export const REPORT_FILES: ReportFile[] = [
    {
        id: 'orgs',
        title: 'Отчёт по организациям',
        description: 'Список организаций с реквизитами, лимитами и количеством пользователей',
        url: '/api/organizations/export/all_organizations',
        fileName: 'AllOrganizations',
    },
    {
        id: 'users',
        title: 'Отчёт по пользователям',
        description: 'Список пользователей с ролями, организациями и данными об активности',
        url: '/api/admin/users/export/users_xlsx',
        fileName: 'AllUsers',
    },
];

export const SEARCHER_LIMITS_EXPORT = '/api/admin/users/export/searcher_requests' satisfies ApiPath;

export type ReportStatus = 'ready' | 'pending' | 'failed';

export interface JournalReport {
    id: string;
    title: string;
    kind: string;
    period: { start: string; end: string } | null;
    createdAt: string;
    author: string;
    format: 'xlsx' | 'csv' | 'pdf';
    size: number | null;
    status: ReportStatus;
}

export interface Paginated<T> {
    items: T[];
    page: number;
    size: number;
    total: number;
    totalPages: number;
}

const saveBlob = (blob: Blob, fileName: string): void => {
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = href;
    link.download = fileName;
    link.rel = 'noopener';

    document.body.append(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(href);
};

const parseFileName = (meta: unknown): string | null => {
    const headers = (meta as FetchBaseQueryMeta | undefined)?.response?.headers;
    const disposition = headers?.get('content-disposition');

    if (!disposition) {
        return null;
    }

    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);

    if (encoded?.[1]) {
        try {
            return decodeURIComponent(encoded[1].trim());
        } catch {
            return null;
        }
    }

    return /filename="?([^";]+)"?/i.exec(disposition)?.[1]?.trim() ?? null;
};

const withExtension = (fileName: string, extension = 'xlsx'): string =>
    fileName.includes('.') ? fileName : `${fileName}.${extension}`;

/* ------------------------------------------------------------- эндпоинты */

export const reportsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getYandexGraphs: build.query<YandexGraphs, { startDate: string; endDate: string }>({
            query: ({ startDate, endDate }) => ({
                url: '/api/reports/report_requests' satisfies ApiPath,
                params: { start_date: startDate, end_date: endDate },
            }),
            transformResponse: (response: YandexGraphsDto) => toYandexGraphs(response),
            providesTags: [{ type: TAG, id: 'GRAPHS' }],
        }),

        getSearcherLimits: build.query<SearcherLimit[], SearcherLimitsQuery>({
            query: ({ inn, since, till, sortBy, orderBy }) => ({
                url: '/api/reports/searcher_limits' satisfies ApiPath,
                params: { inn, since, till, sort_by: sortBy, order_by: orderBy },
            }),
            transformResponse: (response: SearcherLimitsDto) => toSearcherLimits(response),
            providesTags: [{ type: TAG, id: 'LIMITS' }],
        }),

        /** Журнал сформированных отчётов. Пагинация серверная. */
        getReportsJournal: build.query<Paginated<JournalReport>, { page: number; size: number }>({
            query: ({ page, size }) => ({
                url: '/api/reports/journal',
                params: { page, size },
            }),
            providesTags: [{ type: TAG, id: 'JOURNAL' }],
        }),

        /**
         * Готовые выгрузки без периода: организации и пользователи.
         * Мутация, а не query, — результат не кэшируем, файл уходит в браузер сразу.
         */
        downloadReportFile: build.mutation<void, ReportFile>({
            queryFn: async ({ url, title }, _api, _extra, baseQuery) => {
                const result = await baseQuery({ url, responseHandler: (response) => response.blob() });

                if (result.error) {
                    return { error: result.error };
                }

                saveBlob(result.data as Blob, `${title}.xlsx`);

                return { data: undefined };
            },
        }),

        downloadAnalytics: build.mutation<void, { since: string; till: string }>({
            queryFn: async ({ since, till }, _api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: SEARCHER_LIMITS_EXPORT,
                    params: { since, till },
                    responseHandler: (response) => response.blob(),
                });

                if (result.error) {
                    return { error: result.error };
                }

                const headerName = parseFileName(result.meta);

                saveBlob(result.data as Blob, headerName ?? `PlatformAnalytics_${since}_${till}.xlsx`);

                return { data: undefined };
            },
        }),

        /** Скачивание конкретной строки журнала. */
        downloadJournalReport: build.mutation<void, string>({
            queryFn: async (id, _api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: `/api/reports/journal/${id}/download`,
                    responseHandler: (response) => response.blob(),
                });

                if (result.error) {
                    return { error: result.error };
                }

                const headerName = parseFileName(result.meta);

                saveBlob(result.data as Blob, headerName ?? withExtension(`Report_${id}`));

                return { data: undefined };
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetYandexGraphsQuery,
    useGetSearcherLimitsQuery,
    useGetReportsJournalQuery,
    useDownloadReportFileMutation,
    useDownloadAnalyticsMutation,
    useDownloadJournalReportMutation,
} = reportsApi;